import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputSelectField } from "../../../../../components/Shared/InputSelectField";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import moment from "moment";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    visibility: any;
    onClose?: any;
}

type TInputForm = {
    fromDate: null | string,
    toDate: null | string,
    statuses: any[],
};

const getDefaultFormState = () => ({
    fromDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
    statuses: ['pending'],
});

function ReceivedFilter(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>({ ...getDefaultFormState(), ...(props.moduleState.filters ?? []) });
    const [panelState, setPanelState] = useState<boolean>(false);

    const statusOptions = [
        { id: 'pending', label: t('pending') },
        { id: 'completed', label: t('completed') },
        { id: 'failed', label: t('failed') },
    ];

    const formatDateOnly = (value: any): string | null => {
        if ( !value ) {
            return null;
        }

        if ( value instanceof Date && !Number.isNaN(value.getTime()) ) {
            return moment(value).format('YYYY-MM-DD');
        }

        const rawValue = String(value).trim();
        if ( !rawValue ) {
            return null;
        }

        const directDateMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
        if ( directDateMatch ) {
            return directDateMatch[1];
        }

        const parsed = moment(rawValue);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    };

    const onSubmit = async (payloads: TInputForm) => {
        props.setQuery({
            ...props.moduleState,
            page: 1,
            filters: {
                ...payloads,
                fromDate: formatDateOnly(payloads.fromDate),
                toDate: formatDateOnly(payloads.toDate),
            }
        });
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }

        const defaultState = getDefaultFormState();
        setInitFormState({ ...defaultState });
        props.setQuery({
            ...props.moduleState,
            page: 1,
            filters: defaultState
        });
    };

    const onCloseHandler = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            setInitFormState({ ...getDefaultFormState(), ...(props.moduleState.filters ?? []) });
        }
    }, [visibility]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('advance filters') }</h5>
                            <p>{ t("using filters you can archive better result.") }</p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onCloseHandler } blockScroll={ true }
                    style={ { width: '800px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="fromDate"
                                                   type="date"
                                                   label={ t("from date") }
                                                   placeholder={ t("select") + ' ' + t("date").toLowerCase() }
                                                   isClearable />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="toDate"
                                                   type="date"
                                                   label={ t("to date") }
                                                   placeholder={ t("select") + ' ' + t("date").toLowerCase() }
                                                   isClearable />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   options={ statusOptions }
                                                   name="statuses"
                                                   placeholder={ t("select") + ' ' + t("status") }
                                                   label={ t('status') }
                                                   showClear />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '785px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("apply filter") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-filter me-2" />
                                            { t("filter") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onReset }
                                                tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                size={ "small" } severity={ "warning" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onCloseHandler }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

ReceivedFilter.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(ReceivedFilter);
